import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, User, Calendar, MapPin, Heart, Settings } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'

interface SafeUserProfile {
  id: string
  email: string
  first_name: string
  last_name: string
  personality?: string
  created_at: string
  updated_at?: string
  is_premium: boolean
  max_views: number
  max_matches: number
  bio: string
  intro_video_url?: string
  country?: string
  city?: string
  date_of_birth: string
  passions?: string
  profile_completion_percentage: number
  coach: string
  appearance: string
  gender: string
  interested_in: string
  notifications_enabled: boolean
}

export function UsersPage() {
  const { userId } = useParams<{ userId: string }>()

  const { data: user, isLoading, error } = useQuery<SafeUserProfile>({
    queryKey: ['user', userId],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${userId}`)
      return response.data
    },
    enabled: !!userId,
  })

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    
    return age
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">User not found</h3>
            <div className="mt-2 text-sm text-red-700">
              The requested user could not be found.
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link
          to="/reports"
          className="flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Reports
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">User Profile</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info Card */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {user.first_name} {user.last_name}
                  </h2>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
            </div>
            <div className="card-content space-y-4">
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-600">Age: {calculateAge(user.date_of_birth)} years old</span>
              </div>
              
              {(user.country || user.city) && (
                <div className="flex items-center text-sm">
                  <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-gray-600">
                    {user.city && user.country ? `${user.city}, ${user.country}` : user.city || user.country}
                  </span>
                </div>
              )}

              <div className="flex items-center text-sm">
                <Heart className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-600">Interested in: {user.interested_in}</span>
              </div>

              <div className="flex items-center text-sm">
                <Settings className="h-4 w-4 mr-2 text-gray-400" />
                <span className="text-gray-600">Coach: {user.coach}</span>
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Profile Completion</span>
                  <span className="font-medium">{user.profile_completion_percentage}%</span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-600 h-2 rounded-full" 
                    style={{ width: `${user.profile_completion_percentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Premium:</span>
                    <span className={`ml-2 font-medium ${user.is_premium ? 'text-green-600' : 'text-gray-900'}`}>
                      {user.is_premium ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Max Views:</span>
                    <span className="ml-2 font-medium">{user.max_views}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Max Matches:</span>
                    <span className="ml-2 font-medium">{user.max_matches}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Notifications:</span>
                    <span className={`ml-2 font-medium ${user.notifications_enabled ? 'text-green-600' : 'text-gray-900'}`}>
                      {user.notifications_enabled ? 'On' : 'Off'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Bio */}
          {user.bio && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Bio</h3>
              </div>
              <div className="card-content">
                <p className="text-gray-700 whitespace-pre-wrap">{user.bio}</p>
              </div>
            </div>
          )}

          {/* Personality */}
          {user.personality && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Personality</h3>
              </div>
              <div className="card-content">
                <p className="text-gray-700 whitespace-pre-wrap">{user.personality}</p>
              </div>
            </div>
          )}

          {/* Passions */}
          {user.passions && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Passions</h3>
              </div>
              <div className="card-content">
                <div className="flex flex-wrap gap-2">
                  {user.passions.split(';').map((passion, index) => (
                    <span key={index} className="badge badge-info">
                      {passion.trim()}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Intro Video */}
          {user.intro_video_url && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Intro Video</h3>
              </div>
              <div className="card-content">
                <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                  <video
                    controls
                    className="max-w-full max-h-full rounded-lg"
                    src={user.intro_video_url}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          )}

          {/* Account Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Account Information</h3>
            </div>
            <div className="card-content">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">User ID:</span>
                  <span className="ml-2 font-mono text-gray-900">{user.id}</span>
                </div>
                <div>
                  <span className="text-gray-600">Gender:</span>
                  <span className="ml-2 text-gray-900">{user.gender}</span>
                </div>
                <div>
                  <span className="text-gray-600">Appearance:</span>
                  <span className="ml-2 text-gray-900 capitalize">{user.appearance}</span>
                </div>
                <div>
                  <span className="text-gray-600">Member Since:</span>
                  <span className="ml-2 text-gray-900">
                    {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
                {user.updated_at && (
                  <div>
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(user.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
